/**
 * The agents' cursors on the stage — where each one is working and what it is doing, the way
 * collaborators' pointers show in a shared canvas. Every tool run passes through here, from
 * the chat, the bridge or the local agent alike; specialists working side by side each get
 * their own pointer, and the overlay in the editor draws them all.
 */
import type { Vec2, Level } from '../core/hp/types';
import { interiorPoint } from '../state/placement';
import { TOOL_CURSOR_KIND, TOOL_PROGRESS, type CursorKind } from './toolLabels';
import { aliases } from './aliases';
import { SIDE_FROM_INPUT, wallBySide } from './layout';
import { t } from '../i18n/I18n';

export interface CursorState {
  visible: boolean;
  agent: string;
  label: string;
  kind: CursorKind;
  /** Plan point the cursor stands at; null parks it by the dock. */
  point: Vec2 | null;
  /** Bring the point into view if it is off screen (a move or a new item, not a read). */
  reveal: boolean;
  busy: boolean;
  updatedAt: number;
}

/** A cursor before it has moved; the name is the interface's word for an agent, read when the cursor is made. */
const idle = (): CursorState => ({ visible: false, agent: t('#AgentCursorName'), label: '', kind: 'other', point: null, reveal: false, busy: false, updatedAt: 0 });
const num = (value: unknown): number | undefined => typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/** Where a call is aimed, from its arguments alone — before the tool runs. */
export function cursorTargetFor(name: string, input: unknown, level: Level | undefined): Vec2 | null {
  const args = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  if (!level) return null;
  const x = num(args.x), z = num(args.z);
  if (name === 'add_room') { const width = num(args.width) ?? 0, depth = num(args.depth) ?? 0; return x !== undefined && z !== undefined ? { x: x + width / 2, z: z + depth / 2 } : null; }
  if (name === 'add_wall') { const x1 = num(args.x1), z1 = num(args.z1), x2 = num(args.x2), z2 = num(args.z2); return [x1, z1, x2, z2].every(v => v !== undefined) ? { x: (x1! + x2!) / 2, z: (z1! + z2!) / 2 } : null; }
  const roomIndex = num(args.room_index) ?? num(args.index) ?? num(args.room);
  const room = roomIndex !== undefined ? level.rooms.find(candidate => candidate.index === roomIndex) : undefined;
  const wallRef = typeof args.wall_id === 'string' ? args.wall_id : typeof args.wall === 'string' ? args.wall : name === 'update_wall' && typeof args.id === 'string' ? args.id : undefined;
  const side = typeof args.side === 'string' ? SIDE_FROM_INPUT[args.side.toLowerCase()] : undefined;
  const wallId = wallRef ? aliases.resolve('wall', wallRef) : room && side ? wallBySide(level, room, side)?.id : undefined;
  if (wallId) {
    const wall = level.walls.find(candidate => candidate.id === wallId);
    if (wall) {
      const along = num(args.offset) ?? num(args.wall_offset);
      return along !== undefined ? { x: wall.start.x + wall.dir.x * along, z: wall.start.z + wall.dir.z * along } : wall.center;
    }
  }
  if (name === 'update_opening' && typeof args.id === 'string') {
    const opening = level.openings.find(candidate => candidate.id === aliases.resolve('opening', String(args.id)));
    const wall = opening && level.walls.find(candidate => candidate.id === opening.wallId);
    if (opening && wall) return { x: wall.start.x + wall.dir.x * opening.offset, z: wall.start.z + wall.dir.z * opening.offset };
  }
  const itemRef = typeof args.item_id === 'string' ? args.item_id : typeof args.anchor === 'string' ? args.anchor : typeof args.table === 'string' ? args.table : typeof args.id === 'string' && /item/.test(name) ? args.id : undefined;
  if (itemRef) { const item = level.items.find(candidate => candidate.id === aliases.resolve('item', itemRef)); if (item) return item.position; }
  if (name === 'delete' && Array.isArray(args.ids) && args.kind === 'item') { const item = level.items.find(candidate => candidate.id === aliases.resolve('item', String(args.ids![0 as never]))); if (item) return item.position; }
  if (x !== undefined && z !== undefined) return { x, z };
  if (room && room.polygon.length >= 3 && ['add_item', 'update_room', 'set_material', 'focus', 'select', 'get_scene', 'furnish_room', 'set_room_finish', 'place_along_wall', 'add_opening'].includes(name)) return interiorPoint(room.polygon);
  return null;
}

type Listener = () => void;
const ACTIVE_KINDS: ReadonlySet<CursorKind> = new Set(['draw', 'place', 'paint']);

export class AgentCursor {
  private cursors = new Map<string, CursorState>();
  private listeners = new Set<Listener>();
  private hideTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /** Null until a run names its agent: the interface's word for an agent then. */
  private named: string | null = null;
  private visibleList: CursorState[] = [];
  private latest: CursorState | null = null;

  private get agentName(): string { return this.named ?? t('#AgentCursorName'); }
  /** The cursor that moved last — the one place a single-pointer reader looks. */
  get = (): CursorState => this.latest ??= idle();
  /** Every pointer on the stage right now, in the order they appeared. */
  all = (): CursorState[] => this.visibleList;
  subscribe = (fn: Listener): (() => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };

  private set(agent: string, patch: Partial<CursorState>): void {
    const current = this.cursors.get(agent) ?? { ...idle(), agent };
    const next = { ...current, ...patch, agent, updatedAt: Date.now() };
    this.cursors.set(agent, next);
    this.latest = next;
    this.visibleList = [...this.cursors.values()].filter(cursor => cursor.visible);
    for (const fn of this.listeners) fn();
  }

  /** Whose cursor a call without a name belongs to; the label reads "Дизайнер · ставит предмет". */
  setAgent(name: string): void {
    if (name === this.agentName) return;
    const previous = this.agentName;
    this.named = name;
    const cursor = this.cursors.get(previous);
    if (cursor?.visible) { this.hideOne(previous); this.set(name, { ...cursor, agent: name }); }
  }

  begin(name: string, input: unknown, level: Level | undefined, agent = this.agentName): void {
    const timer = this.hideTimers.get(agent);
    if (timer) { clearTimeout(timer); this.hideTimers.delete(agent); }
    const point = cursorTargetFor(name, input, level);
    const kind = TOOL_CURSOR_KIND[name] ?? 'other';
    const previous = this.cursors.get(agent);
    this.set(agent, { visible: true, label: TOOL_PROGRESS[name] ?? name, kind, point: point ?? (kind === 'read' ? previous?.point ?? null : null), reveal: ACTIVE_KINDS.has(kind), busy: true });
  }

  finish(name: string, summary: string, anchor: Vec2 | null | undefined, ok: boolean, agent = this.agentName): void {
    const previous = this.cursors.get(agent);
    this.set(agent, { label: ok ? summary : t('#AgentCursorFailed', TOOL_PROGRESS[name]?.toLowerCase() ?? name), point: anchor ?? previous?.point ?? null, busy: false, reveal: !!anchor && ACTIVE_KINDS.has(TOOL_CURSOR_KIND[name] ?? 'other') });
    this.hideLater(agent);
  }

  /** The run is over: every cursor lingers a moment, then goes. */
  done(): void { for (const agent of this.cursors.keys()) this.hideLater(agent, 1200); }

  hide(): void { for (const agent of [...this.cursors.keys()]) this.hideOne(agent); }

  private hideOne(agent: string): void {
    const timer = this.hideTimers.get(agent);
    if (timer) { clearTimeout(timer); this.hideTimers.delete(agent); }
    if (this.cursors.get(agent)?.visible) this.set(agent, { visible: false, busy: false });
  }

  private hideLater(agent: string, ms = 2800): void {
    const timer = this.hideTimers.get(agent);
    if (timer) clearTimeout(timer);
    this.hideTimers.set(agent, setTimeout(() => { this.hideTimers.delete(agent); this.hideOne(agent); }, ms));
  }
}

export const agentCursor = new AgentCursor();
